const GEOCODE_URL = "https://geocoding-api.open-meteo.com/v1/search";
const CACHE_KEY = "ga-geocode-cache-v1";
const CACHE_MAX = 40;

/**
 * @typedef {object} PlaceResult
 * @property {number} id
 * @property {string} name
 * @property {string} label
 * @property {number} latitude
 * @property {number} longitude
 * @property {string} [timezone]
 * @property {string} [country]
 * @property {string} [admin1]
 */

/**
 * Search places via Open-Meteo geocoding (no API key).
 * @param {string} query
 * @param {{ count?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<PlaceResult[]>}
 */
export async function searchPlaces(query, opts = {}) {
  const q = normalizePlaceQuery(String(query || "").trim());
  if (q.length < 2) return [];

  const cached = readCache(q);
  if (cached) return cached;

  const url = new URL(GEOCODE_URL);
  url.searchParams.set("name", q);
  url.searchParams.set("count", String(opts.count ?? 6));
  url.searchParams.set("language", "en");
  url.searchParams.set("format", "json");

  const res = await fetch(url.toString(), { signal: opts.signal });
  if (!res.ok) {
    throw new Error(`Place lookup failed (${res.status})`);
  }

  const data = await res.json();
  const results = (data.results || []).map(normalizeResult).filter(Boolean);
  writeCache(q, results);
  return results;
}

/**
 * Map common aliases the geocoder misses (e.g. "Washington, DC").
 * @param {string} q
 */
function normalizePlaceQuery(q) {
  const compact = q.replace(/\s+/g, " ").trim();
  const lower = compact.toLowerCase();
  // Open-Meteo returns nothing for "Washington, DC" / "Washington DC"
  if (
    lower === "washington, dc" ||
    lower === "washington dc" ||
    lower === "washington, d.c." ||
    lower === "washington d.c" ||
    lower === "washington, d.c" ||
    lower === "washington d.c."
  ) {
    return "Washington D.C.";
  }
  return compact;
}
/** @param {any} r @returns {PlaceResult | null} */
function normalizeResult(r) {
  if (r == null || r.latitude == null || r.longitude == null) return null;
  const parts = [r.name, r.admin1, r.country].filter(Boolean);
  // Avoid "City, City" duplication
  const label = [...new Set(parts)].join(", ");
  return {
    id: r.id,
    name: r.name,
    label,
    latitude: r.latitude,
    longitude: r.longitude,
    timezone: r.timezone || "",
    country: r.country || "",
    admin1: r.admin1 || "",
  };
}

function readCache(query) {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    const hit = map[query.toLowerCase()];
    return Array.isArray(hit) ? hit : null;
  } catch {
    return null;
  }
}

function writeCache(query, results) {
  try {
    const key = query.toLowerCase();
    let map = {};
    try {
      map = JSON.parse(localStorage.getItem(CACHE_KEY) || "{}") || {};
    } catch {
      map = {};
    }
    map[key] = results;
    const keys = Object.keys(map);
    if (keys.length > CACHE_MAX) {
      for (const k of keys.slice(0, keys.length - CACHE_MAX)) {
        delete map[k];
      }
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
}
